<template>
  <div class="box-conversation-body">
    <div
      ref="contentContainer"
      class="conversation-body octadesk-content-container"
      @dragenter="startDrag"
      @dragover="startDrag"
      @dragleave="hideDropZone"
    >
      <div class="loader-container" v-if="showLoader">
        <div class="loader-container-inner">
          <VueSkeletonLoader
            type="circle"
            :width="35"
            :height="35"
            animation="fade"
          />

          <div class="loader-container-inner-text">
            <VueSkeletonLoader
              rounded
              width="100%"
              height="115px"
              class="text-skeleton"
            />

            <VueSkeletonLoader
              rounded
              width="100%"
              height="55px"
              class="text-skeleton"
            />
          </div>
        </div>
      </div>

      <ol
        v-else
        class="octadesk-chat-discussion"
        :class="[dragging ? 'drag-active' : '']"
      >
        <template v-for="event in events">
          <ConversationEvent
            v-if="
              events &&
              ((chat && chat.status !== 4) ||
                event.type === 'ConversationMessage')
            "
            :key="`${event.time}-${event.property}`"
            :event="event"
          ></ConversationEvent>
        </template>

        <transition name="slide-fade" mode="out-in">
          <SurveyModal v-if="showSurveyModal" />

          <SurveyResponse v-if="showSurveyResponse" />
        </transition>

        <ConversationTyping></ConversationTyping>

        {{
          scrollInTyping
        }}

        <li
          class="powered-by"
          :class="[
            (!hideDrag && !dragging) || datePickerValid.status ? 'active' : ''
          ]"
        >
          <FooterPoweredBy />
        </li>
      </ol>
    </div>

    <button
      v-show="dragError && !hideDrag"
      class="send-archive-error"
      :style="style"
      @click="newUpload"
    >
      {{ $t('conversation.dropzone.buttonSendAgain') }}
    </button>

    <div v-show="commentError" class="comment-length-error" :style="style">
      {{ $t('conversation.dropzone.commentError') }}
    </div>

    <vue2Dropzone
      v-if="!inBotConversation && dropzoneOptions"
      id="dropzone-chat"
      ref="dropzone"
      :class="{ 'octadesk-app-hide': hideDrag }"
      class="attachment-preview octadesk-app-attachment-preview"
      :options="dropzoneOptions"
      @vdropzone-removed-file="onRemoveFile"
      @vdropzone-success="onAddSuccess"
      @vdropzone-file-added="onAddedFile"
      @vdropzone-drag-leave="dragLeave"
      @vdropzone-error="onFileAddError"
      @vdropzone-drag-end="setFileDragging(false)"
      @vdropzone-drag-over="setFileDragging(false)"
    ></vue2Dropzone>
  </div>
</template>

<script>
  import Vue from 'vue'

  import vue2Dropzone from 'vue2-dropzone'

  import VueSkeletonLoader from 'skeleton-loader-vue'

  import 'vue2-dropzone/dist/vue2Dropzone.min.css'

  import { mapGetters, mapActions } from 'vuex'

  import { roomStatus } from '@/helpers'

  import ConversationEvent from './ConversationEvent'

  import ConversationTyping from './ConversationTyping'

  import SurveyModal from '@/app/components/SurveyModal'

  import SurveyResponse from '@/app/components/SurveyResponse'

  import FooterPoweredBy from '@/app/components/FooterPoweredBy'
  import { services } from '@octadesk-tech/services'

  let verifyWaitingMessageTimeout

  export default {
    components: {
      vue2Dropzone,
      VueSkeletonLoader,
      ConversationEvent,
      ConversationTyping,
      SurveyModal,
      SurveyResponse,
      FooterPoweredBy
    },
    data() {
      return {
        showAgentThumb: false,
        hideDrag: true,
        dragError: false,
        hideTimeout: undefined,
        dropzoneOptions: undefined
      }
    },
    computed: {
      ...mapGetters([
        'commentError',
        'settings',
        'kbEnabled',
        'chat',
        'chatStatus',
        'typing',
        'events',
        'waitingRuleExecuted',
        'waitingMessageDelay',
        'hasWaitingMessage',
        'company',
        'conversationFooterHeight',
        'dragging',
        'inBotConversation',
        'datePickerValid',
        'canAskSurvey',
        'isSurveyAnswered'
      ]),
      showLoader() {
        return !this.events.length && this.inBotConversation
      },
      style() {
        let newRBGAColor = this.settings.bgColorRGBA.split(',')

        newRBGAColor.pop()

        newRBGAColor.push(' 1')

        return `border: 1px solid rgba(${newRBGAColor}); color: rgba(${newRBGAColor});`
      },
      scrollInTyping() {
        if (this.typing) {
          this.scrollTop()
        }

        return ''
      },
      showSurveyModal() {
        return this.canAskSurvey && !this.isSurveyAnswered
      },
      showSurveyResponse() {
        return this.isSurveyAnswered
      }
    },
    watch: {
      chatStatus() {
        if (
          this.chatStatus === roomStatus.waiting ||
          (this.chatStatus === roomStatus.started && !this.waitingRuleExecuted)
        ) {
          this.verifyWaitingMessage()
        }
      },
      conversationFooterHeight() {
        if (this.$refs.contentContainer) {
          if (
            this.$refs.contentContainer.scrollHeight -
              this.$refs.contentContainer.scrollTop -
              this.$refs.contentContainer.clientHeight <=
            50
          ) {
            this.scrollTop()
          }
        }
      }
    },
    updated() {
      this.scrollTop()
    },
    mounted() {
      services
        .createClient('widgetBff')
        .then(
          client =>
            (this.dropzoneOptions = {
              url: `${client.defaults.baseURL}/upload`,
              maxFilesize: 30,
              dictDefaultMessage: `<i class='icon-upload'></i> <p>${this.$t(
                'conversation.dropzone.dictDefaultMessage'
              )}</p>`,
              dictFileTooBig: this.$t('conversation.dropzone.dictFileTooBig'),
              dictResponseError: this.$t(
                'conversation.dropzone.dictResponseError'
              ),
              dictCancelUpload: this.$t(
                'conversation.dropzone.dictCancelUpload'
              ),
              dictCancelUploadConfirmation: this.$t(
                'conversation.dropzone.dictCancelUploadConfirmation'
              ),
              dictRemoveFile: this.$t('conversation.dropzone.dictRemoveFile'),
              addRemoveLinks: true,
              clickable: true,
              headers: {
                AppSubdomain: this.company,
                'X-Requested-With': null
              }
            })
        )
        .then(() =>
          this.$nextTick(
            () => (Vue.prototype.$dropzone_ref = this.$refs.dropzone)
          )
        )
    },
    methods: {
      ...mapActions([
        'sendMessage',
        'addAttachment',
        'removeAttachment',
        'clearAttachments',
        'executeWaitingMessage',
        'setFileDragging',
        'setUploadInProcess'
      ]),
      scrollTop() {
        if (this.$refs.contentContainer) {
          let scrollHeight = this.$refs.contentContainer.scrollHeight

          this.$refs.contentContainer.scrollTop = scrollHeight
        }
      },
      onAddSuccess(file, response) {
        this.addAttachment(response)

        if (this.$refs.dropzone.getUploadingFiles().length === 0) {
          this.$refs.dropzone.removeAllFiles()

          this.hideDropZone()

          this.sendMessage().then(() => {
            this.clearAttachments()
          })

          this.setUploadInProcess(false)
        }
      },
      onRemoveFile(file) {
        this.dragError = false

        this.setFileDragging(false)

        this.hideDropZone()

        this.removeAttachment(file)
      },
      onAddedFile() {
        this.setFileDragging(false)
        this.setUploadInProcess(true)

        this.hideDrag = false
      },
      countFiles() {
        return this.$refs.dropzone.dropzone.files.length
      },
      startDrag() {
        if (this.inBotConversation) return

        this.setFileDragging(true)

        this.hideDrag = false
      },
      dragLeave() {
        this.setFileDragging(false)

        this.hideDropZone()
      },
      onFileAddError(file, message) {
        this.dragError = true

        this.setFileDragging(false)

        console.error(`error on file upload: `, message)
      },
      hideDropZone() {
        if (this.hideTimeout) {
          clearTimeout(this.hideTimeout)
        }

        this.hideTimeout = setTimeout(() => {
          if (
            (!this.countFiles() && !this.dragging) ||
            this.countFiles() === 0
          ) {
            this.hideDrag = true
          }
        }, 500)
      },
      verifyWaitingMessage() {
        if (this.hasWaitingMessage) {
          if (verifyWaitingMessageTimeout) {
            clearTimeout(verifyWaitingMessageTimeout)

            verifyWaitingMessageTimeout = undefined
          }

          verifyWaitingMessageTimeout = setTimeout(() => {
            if (!this.waitingRuleExecuted) {
              const kbEnabledCondition =
                this.kbEnabled && this.chatStatus === roomStatus.waiting

              const kbDisabledCondition =
                !this.kbEnabled &&
                (this.chatStatus === roomStatus.started ||
                  this.chatStatus === roomStatus.waiting)

              if (kbEnabledCondition || kbDisabledCondition) {
                this.executeWaitingMessage()
              }
            } else {
              clearTimeout(verifyWaitingMessageTimeout)

              verifyWaitingMessageTimeout = undefined
            }
          }, this.waitingMessageDelay)
        }
      },
      newUpload() {
        this.dragError = false

        this.hideDropZone()

        this.$refs.dropzone.removeAllFiles()

        this.sendMessage().then(() => {
          this.clearAttachments()
        })
        document.querySelector('.dropzone').click()
      }
    }
  }
</script>

<style lang="scss">
  .loader-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: flex-end;
    &-inner {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      &-text {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        flex: 1;
        .text-skeleton {
          border-radius: 0.5rem 1.5rem 1.5rem 0.5rem;
        }
      }
    }
  }
  div.box-conversation-body {
    position: relative;
    div.conversation-body {
      height: 100%;
      background: transparent linear-gradient(180deg, #ffffff 0%, #f7f7f7 100%)
        0% 0% no-repeat padding-box;
      ol.octadesk-chat-discussion {
        &.drag-active {
          filter: blur(2px);
        }
        li.powered-by {
          transition: 0.5s;
          &.active {
            margin-bottom: 44px;
          }
        }
      }
    }
    div.comment-length-error {
      width: 100%;
      padding: 8px 16px;
      position: absolute;
      bottom: 0px;
      right: 0px;
      z-index: 3;
      font-size: 11px;
      font-weight: 400;
      cursor: pointer;
      background-color: #fff1f3;
      border: none;
      color: #fe1b31;
    }
    button.send-archive-error {
      width: 160px;
      height: 28px;
      border-radius: 30px;
      position: absolute;
      bottom: 8px;
      right: 40px;
      z-index: 3;
      font-size: 11px;
      font-weight: 400;
      cursor: pointer;
      transition: 0.5s;
      &:hover {
        color: #666666 !important;
        background-color: #ffffff;
      }
    }
    div.octadesk-app-attachment-preview {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      flex-direction: column;
      width: 100%;
      height: 100%;
      min-height: auto;
      left: 0px;
      bottom: 0px;
      position: absolute;
      z-index: 2;
      border: none !important;
      overflow: auto;
      padding: 0px;
      background: transparent !important;
      div.dz-message {
        opacity: 0;
      }
      div.dz-image,
      div.dz-details,
      div.dz-error-mark {
        display: none !important;
      }
      &.dz-drag-hover {
        justify-content: center;
        background: rgba(255, 255, 255, 0.9) !important;
        div.dz-message {
          margin: 0;
          opacity: 1 !important;
          display: block;
          span {
            display: flex;
            justify-content: center;
            align-items: center;
            text-align: center;
            flex-direction: column;
            i {
              font-size: 40px;
              color: #656565;
            }
            p {
              font-size: 16px;
              color: #0062f9;
              font-weight: 700;
              margin: 10px 0px 0px 0px;
            }
          }
        }
        &:before {
          content: '';
          width: 98%;
          height: 98%;
          position: absolute;
          top: 1%;
          left: 1%;
          border: 2px dashed #0d6af9;
        }
        div.dz-preview {
          div.dz-progress,
          div.dz-error-message {
            display: none !important;
          }
        }
        & + button.send-archive-error,
        & ~ button.send-archive-error {
          display: none;
        }
      }
      /* &.dz-started {
      height: auto;
    } */
      div.dz-preview {
        width: 100%;
        min-height: 44px;
        margin: 0px;
        overflow: hidden;
        position: absolute;
        bottom: 0px;
        left: 0px;
        background: transparent !important;
        div.dz-progress,
        div.dz-error-message {
          opacity: 0;
          pointer-events: none;
          position: absolute;
          top: auto;
          right: auto;
          bottom: 0px;
          left: 0px;
        }
        div.dz-progress {
          z-index: 3;
          margin: 0px;
          padding: 0px 16px 10px 16px;
          width: 100%;
          height: 44px;
          display: flex;
          align-items: flex-end;
          overflow: unset;
          animation: none !important;
          border-radius: 0px;
          background: #ffffff;
          span {
            height: 5px;
            z-index: 5;
            border-radius: 5px;
            position: relative !important;
            background: #008fff !important;
          }
          &:before {
            content: 'Estamos enviando seu arquivo...';
            position: absolute;
            top: 8px;
            font-size: 12px;
            font-weight: 400;
            color: #b3bbc2;
          }
          &:after {
            content: '';
            width: 100%;
            height: 5px;
            border-radius: 5px;
            background-color: #d8d8d8;
          }
        }
        div.dz-error-message {
          transition: 0.5s;
          z-index: 2;
          border-radius: 0px;
          display: flex !important;
          justify-content: flex-start;
          align-items: center;
          padding: 8px 16px;
          background: #fef4f6;
          &:before,
          span {
            color: #fe1b31;
          }
          &:before {
            font-family: 'icomoon' !important;
            content: '\e906';
            font-size: 20px;
            margin-right: 8px;
          }
          span {
            max-width: 122px;
            font-size: 12px;
            text-align: left;
          }
        }
        a.dz-remove {
          position: absolute;
          z-index: 4;
          width: 13px;
          height: 13px;
          bottom: 18px;
          right: 16px;
          border: none;
          opacity: 1;
          padding: 0px;
          text-indent: -9999px;
          &:before,
          &:after {
            content: '';
            width: 100%;
            height: 2px;
            position: absolute;
            top: 8px;
            right: 0px;
            transition: 0.5s;
            background-color: #a09b9c;
          }
          &:before {
            transform: rotate(45deg);
          }
          &:after {
            transform: rotate(-45deg);
          }
          &:hover:before,
          &:hover:after {
            background-color: #000;
          }
        }
        &.dz-error div.dz-error-message,
        &.dz-processing:not(.dz-error) div.dz-progress {
          opacity: 1;
          pointer-events: all;
        }
        &.dz-processing:not(.dz-error) a.dz-remove {
          bottom: 24px !important;
        }
      }
    }
  }

  .clear-typing-size {
    margin-bottom: 40px;
  }

  .octadesk-app-messenger .octadesk-content-container {
    margin-top: 0 !important;
  }

  .octadesk-app-messenger .octadesk-content-container .dz-image-preview {
    display: block;
  }

  .octadesk-app-messenger .octadesk-content-container .dz-image img {
    margin: 0 auto;
  }

  .slide-fade-enter-active {
    transition: all 0.3s ease;
  }
  .slide-fade-leave-active {
    transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
  }
  .slide-fade-enter,
  .slide-fade-leave-to {
    transform: translateY(10px);
    opacity: 0;
  }

  .octadesk-chat-attachment:after,
  .octadesk-chat-attachment:before {
    content: '';
    clear: both;
    display: block;
  }

  .octadesk-chat-attachment-tag {
    max-width: 85%;
    float: left;
    margin-bottom: 0.1875rem;
    padding: 0.3125rem 0.625rem;
    background-color: #fff;
    border: 1px solid #ddd;
    border-radius: 0.1875rem;
    color: #666;
    text-decoration: none;
  }

  .octadesk-chat-attachment:last-child .octadesk-chat-attachment-tag {
    margin-bottom: 0;
  }

  .octadesk-chat-attachment img {
    max-width: 180px;
  }
  .octadesk-chat-self {
    img {
      border-radius: 29px 29px 8px 29px;
    }
    &.last img,
    &.medium img {
      border-radius: 29px 8px 8px 29px;
    }
  }
  .octadesk-chat-other {
    img {
      border-radius: 8px 29px 29px 29px;
    }
    &.first img,
    &.medium img {
      border-radius: 8px 29px 29px 8px;
    }
  }

  .dz-hidden-input {
    display: none;
  }
</style>
